import Footer from "@/components/layout/Footer";
import Header from "@/components/layout/Header";
import handleError from "@/utils/handleError";
import { getLanguageName, getProficiencyLevel } from "@/utils/language";
import { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { User } from "snudbud-backbone-sdk";
import AvailabilitySchedule from "../components/profile/AvailabilitySchedule";
import LanguagesSection from "../components/profile/LanguagesSection";
import ProfileHeader from "../components/profile/ProfileHeader";
import { useAPI } from "../contexts/APIContext";

// Redirect component for the /profile route
export function ProfileRedirect() {
  const { user, loading } = useAPI();

  // Wait for loading to complete before redirecting
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-pulse text-gray-500">Loading profile...</div>
        </div>
      </div>
    );
  }

  // Redirect to the profile with userId once loaded
  return <Navigate to={`/profile/${user?.userId}`} replace />;
}

export default function ProfilePage() {
  const { userId } = useParams<{ userId: string }>();
  const { user, userProfile, api } = useAPI();
  const [profile, setProfile] = useState<User.FetchProfileReturn>();
  const [isProfileLoading, setIsProfileLoading] = useState(true);
  const [showMoreActions, setShowMoreActions] = useState(false);
  const itemsPerPage = 5;

  const isOwnProfile = user?.userId === userId;

  useEffect(() => {
    if (!userId) return;

    const loadProfileData = async () => {
      setIsProfileLoading(true);
      try {
        // Use cached profile for current user, otherwise fetch from API
        if (isOwnProfile && userProfile) return setProfile(userProfile);

        const fetchedProfile = await api.user.fetchProfile({ userId });
        setProfile(fetchedProfile);
      } catch (error) {
        handleError(error);
      } finally {
        setIsProfileLoading(false);
      }
    };

    loadProfileData();
  }, [userId]);

  const handleToggleMoreActions = () => {
    setShowMoreActions(!showMoreActions);
  };

  const handleBlockUser = async () => {
    if (!userId) return;

    try {
      await api.user.block({ userId });

      // Update the profile with isBlockedByUser
      if (profile) setProfile({ ...profile, isBlockedByUser: true });

      setShowMoreActions(false);
    } catch (error) {
      handleError(error);
    }
  };

  const handleUnblockUser = async () => {
    if (!userId) return;

    try {
      await api.user.unblock({ userId });

      // Update the profile with isBlockedByUser set to false
      if (profile) {
        setProfile({
          ...profile,
          isBlockedByUser: false,
        });
      }

      setShowMoreActions(false);
    } catch (error) {
      handleError(error);
    }
  };

  if (isProfileLoading && !profile) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-pulse text-gray-500">Loading profile...</div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-gray-700">Profile not found.</div>
        </div>
      </div>
    );
  }

  // Process languages into speaking and learning categories
  const speakingLanguages =
    profile.languages
      ?.filter((lang) => !lang.learning)
      .map((lang) => ({
        code: lang.languageCode,
        name: getLanguageName(lang.languageCode),
        proficiency: getProficiencyLevel(lang.proficiency),
      })) || [];

  const learningLanguages =
    profile.languages
      ?.filter((lang) => lang.learning)
      .map((lang) => ({
        code: lang.languageCode,
        name: getLanguageName(lang.languageCode),
        proficiency: getProficiencyLevel(lang.proficiency),
      })) || [];

  return (
    <div className="min-h-screen flex flex-col bg-white text-gray-800">
      <Header />

      <main className="flex-1 max-w-screen-md mx-auto px-4 py-8 w-full">
        {/* Profile Header Component */}
        <ProfileHeader
          profile={profile}
          isOwnProfile={isOwnProfile}
          showMoreActions={showMoreActions}
          onToggleMoreActions={handleToggleMoreActions}
          onBlockUser={handleBlockUser}
          onUnblockUser={handleUnblockUser}
        />

        {/* Languages Section Component */}
        <LanguagesSection speakingLanguages={speakingLanguages} learningLanguages={learningLanguages} />

        {/* Availability Schedule Component */}
        <AvailabilitySchedule userId={profile.userId || ""} itemsPerPage={itemsPerPage} isOwnProfile={isOwnProfile} />
      </main>

      <Footer />
    </div>
  );
}
